import {useCallback, useSyncExternalStore} from 'react';

export type FullscreenApi = {
  readonly isSupported: boolean;
  readonly isFullscreen: boolean;
  /** Resolves `true` once the document is fullscreen; `false` when unsupported or refused. */
  readonly request: () => Promise<boolean>;
  readonly exit: () => Promise<void>;
};

const subscribe = (onChange: () => void): (() => void) => {
  document.addEventListener('fullscreenchange', onChange);
  return () => {
    document.removeEventListener('fullscreenchange', onChange);
  };
};

const isFullscreenActive = (): boolean => Boolean(document.fullscreenElement);

const getServerSnapshot = (): boolean => false;

const isFullscreenSupported = (): boolean =>
  typeof document.documentElement.requestFullscreen === 'function' &&
  document.fullscreenEnabled === true;

/**
 * Feature-agnostic Fullscreen API wrapper. `request` reaches the browser synchronously so it
 * stays inside the user gesture that triggered it; every failure resolves quietly.
 */
export const useFullscreen = (): FullscreenApi => {
  const isFullscreen = useSyncExternalStore(subscribe, isFullscreenActive, getServerSnapshot);

  const request = useCallback((): Promise<boolean> => {
    if (!isFullscreenSupported()) {
      return Promise.resolve(false);
    }
    if (isFullscreenActive()) {
      return Promise.resolve(true);
    }
    try {
      return document.documentElement.requestFullscreen({navigationUI: 'hide'}).then(
        () => true,
        () => false,
      );
    } catch {
      return Promise.resolve(false);
    }
  }, []);

  const exit = useCallback((): Promise<void> => {
    if (!isFullscreenActive() || typeof document.exitFullscreen !== 'function') {
      return Promise.resolve();
    }
    try {
      return document.exitFullscreen().catch(() => undefined);
    } catch {
      return Promise.resolve();
    }
  }, []);

  return {isSupported: isFullscreenSupported(), isFullscreen, request, exit};
};
